import { useState, FC } from "react";
import {
  useForm,
  Controller,
  useFieldArray,
  SubmitHandler,
  FieldErrors,
} from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Modal,
  Tooltip,
} from "@mui/material";
import { AddCircle } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./rsvp.module.scss";
import { submitGuests, validateGuests } from "../../services/api";
import { GuestSubmission } from "../../models/GuestSubmission";
import SubmissionModal from "../../Components/SubmissionModal/submissionModal";

const guestSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  isAttending: yup.boolean().required(),
  allergies: yup.string().optional(),
  songToGetOnTheDanceFloor: yup.string().optional(),
});

const schema = yup.object({
  guests: yup.array().of(guestSchema).required(),
});

type FormValues = yup.InferType<typeof schema>;

const defaultGuest: GuestSubmission = {
  firstName: "",
  lastName: "",
  allergies: "",
  songToGetOnTheDanceFloor: "",
  isAttending: true,
};

interface GuestFieldsProps {
  index: number;
  control: any;
  errors: FieldErrors<FormValues>;
  onRemove?: () => void;
  showError: boolean;
}

const GuestFields: FC<GuestFieldsProps> = ({
  index,
  control,
  errors,
  onRemove,
  showError,
}) => (
  <Box className={styles.guestBox}>
    <div className={styles.guestDetails}>
      <div className={styles.nameFields}>
        <Controller
          name={`guests.${index}.firstName`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                const filtered = e.target.value.replace(/[^a-zA-Z'\-\s]/g, '');
                field.onChange(filtered);
              }}
              onBlur={(e) => {
                field.onChange(e.target.value.trim());
                field.onBlur();
              }}
              label="First name"
              variant="standard"
              fullWidth
              required
              inputProps={{ maxLength: 25 }}
            />
          )}
        />
        <Controller
          name={`guests.${index}.lastName`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                const filtered = e.target.value.replace(/[^a-zA-Z'\-\s]/g, '');
                field.onChange(filtered);
              }}
              onBlur={(e) => {
                field.onChange(e.target.value.trim());
                field.onBlur();
              }}
              label="Last name"
              variant="standard"
              fullWidth
              required
              inputProps={{ maxLength: 25 }}
            />
          )}
        />
      </div>

      <div className={styles.radioGroup}>
        <Typography color="rgba(0, 0, 0, 0.7)" variant="body1">
          Will you be attending?
        </Typography>
        <Controller
          name={`guests.${index}.isAttending`}
          control={control}
          render={({ field }) => (
            <RadioGroup {...field} row>
              <FormControlLabel
                value={true}
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value={false}
                control={<Radio color="error" />}
                label="No"
              />
            </RadioGroup>
          )}
        />
      </div>
      <Controller
        name={`guests.${index}.allergies`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Allergies (if any)"
            variant="standard"
            onChange={(e) => {
              const filtered = e.target.value.replace(/[^a-zA-Z'\-\s]/g, '');
              field.onChange(filtered);
            }}
            multiline
            fullWidth
            inputProps={{ maxLength: 75 }}
          />
        )}
      />

      <Controller
        name={`guests.${index}.songToGetOnTheDanceFloor`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="What song will get you on the dance floor?"
            variant="standard"
            multiline
            fullWidth
            onChange={(e) => {
              const filtered = e.target.value.replace(/;/g, '');
              field.onChange(filtered);
            }}
            inputProps={{ maxLength: 75 }}
          />
        )}
      />
    </div>

    {showError && errors.root && (
      <Box sx={{ maxWidth: 350, mt: 2 }}>
        <Alert severity="error">{errors.root.message}</Alert>
      </Box>
    )}

    {onRemove && (
      <div className={styles.removeGuestButton}>
        <Button variant="contained" color="error" onClick={onRemove}>
          Remove Guest
        </Button>
      </div>
    )}
  </Box>
);

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  guests?: GuestSubmission[];
}

const ConfirmationModal: FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  guests,
}) => (
  <Modal open={open} onClose={onClose} aria-labelledby="rsvp-confirmation">
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        boxShadow: 24,
        p: 2,
        width: 300,
      }}
    >
      <Typography align={"center"} id="rsvp-confirmation" variant="h6" mb={2}>
        Confirmation Summary
      </Typography>
      <div style={{ position: "absolute", top: 14, right: 15, cursor: 'pointer' }}>
        <CloseIcon onClick={onClose} />
      </div>
      {guests?.map((g, idx) => (
        <Typography key={idx} variant="body1">
          {`${g.firstName} ${g.lastName} ${g.isAttending ? "is" : "isn't"
            } attending.`}
        </Typography>
      ))}
      <Box sx={{ mt: 3, padding: "0 100px" }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={onConfirm}
        >
          Confirm
        </Button>
      </Box>
    </Box>
  </Modal>
);

const Rsvp: FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: { guests: [defaultGuest] },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "guests" });
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [guestsPendingConfirm, setGuestsPendingConfirm] =
    useState<GuestSubmission[]>();

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const guestNames = data.guests.map(g => 
      `${g.firstName.toLowerCase().trim()} ${g.lastName.toLowerCase().trim()}`
    );
    const duplicates = guestNames.filter((name, index) => 
      guestNames.indexOf(name) !== index
    );

    if (duplicates.length > 0) {
      setError("root", {
        type: "manual",
        message: "Duplicate guests detected. Please ensure each guest is listed only once.",
      });
      setIsConfirmModalOpen(false);
      return;
    }

    const areGuestsValid = await validateGuests(data.guests);
    if (!areGuestsValid) {
      setError("root", {
        type: "manual",
        message: "One or more guests are not on the guest list. Please check the spelling and try again. If the issue persists, contact us directly.",
      });
      setIsConfirmModalOpen(false);
      return;
    }

    setGuestsPendingConfirm(data.guests);
    clearErrors("root");
    setIsConfirmModalOpen(true);
  };

  const [areGuestsValid, setAreGuestsValid] = useState(true);
  
  const handleConfirm = async () => {
    if (!guestsPendingConfirm) return;
    setAreGuestsValid(await validateGuests(guestsPendingConfirm));
    if (!areGuestsValid) {
      setError("root", {
        type: "manual",
        message: "One or more guests are not on the guest list. Please check the spelling and try again. If the issue persists, contact us directly.",
      });
      setIsConfirmModalOpen(false);
      return;
    }

    const success = await submitGuests(guestsPendingConfirm);
    setIsConfirmModalOpen(false);
    reset({ guests: [defaultGuest] });
    setIsSubmissionSuccessful(success);
    setIsSuccessModalOpen(true);
  };

  return (
    <div className={styles.rsvpSection} id="rsvp">
      <Box className={styles.box}>
        <header className={styles.header}>
          <Typography align="center" fontFamily="Crimson Pro" variant="h4">
            RSVP
          </Typography>
          <Chip
            label="RSVP by March 16th 2026"
            className={styles.chip}
            size="small"
          />
        </header>

       
        <form onSubmit={handleSubmit(onSubmit)}>
          {fields.map((guest, index) => (
            <GuestFields
              key={guest.id}
              index={index}
              control={control}
              errors={errors}
              showError={!!errors.root}
              onRemove={fields.length > 1 ? () => remove(index) : undefined}
            />
          ))}

          <div className={styles.addGuestSection}>
            <Tooltip
              title={fields.length >= 8 ? "Maximum guest limit reached (8 guests)" : ""}
              arrow
            >
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  startIcon={<AddCircle color="primary" fontSize="large" />}
                  onClick={() => {
                    clearErrors("root");
                    append(defaultGuest);
                  }}
                  disabled={fields.length >= 8}
                >
                  Add Guest
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              type="submit"
            >
              Submit
            </Button>
          </div>
        </form>
      </Box>

      <ConfirmationModal
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirm}
        guests={guestsPendingConfirm}
      />
      <SubmissionModal
        isOpen={isSuccessModalOpen}
        handleClose={() => setIsSuccessModalOpen(false)}
        isSuccessful={isSubmissionSuccessful}
      />
    </div>
  );
};

export default Rsvp;
